import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ImpersonationService } from 'projects/service/src/app/shared/services/impersonation.service';

// The yellow pill while impersonating (Dev / Test / Stage): who you are working as, until when, and Stop. The session stops by
// itself 30 s before the token expires, so the app never signs out with the admin's token lost.
const AUTO_STOP_BEFORE_MS = 30_000;

@Component({
  selector: 'app-impersonation-banner',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DatePipe],
  template: `
    @if (session(); as s) {
      <div class="impersonation-frame" aria-hidden="true"></div>
      <div class="impersonation-pill" role="status">
        <i class="fas fa-user-secret" aria-hidden="true"></i>
        <span class="who" [title]="s.displayName + ' (' + s.userName + ')'">
          Impersonating <strong>{{ s.displayName }}</strong> until {{ s.expiration | date: 'MMM d, h:mm a' }}
        </span>
        <button type="button" class="btn btn-danger btn-sm fw-bold" (click)="stop()" [disabled]="stopping()"
          title="Go back to your own account">
          <i class="fas fa-sign-out-alt me-1" aria-hidden="true"></i>{{ stopping() ? 'Stopping…' : 'Stop impersonating' }}
        </button>
      </div>
    }
  `,
  // Fixed, over the empty middle of the header: nothing on the page moves. The yellow frame round the window says
  // "not your own account" on every screen without taking any room (clicks go through it).
  styles: [`
    .impersonation-frame { position: fixed; inset: 0; border: 3px solid #ffc107; pointer-events: none; z-index: 2000; }
    .impersonation-pill {
      position: fixed; top: 6px; left: 50%; transform: translateX(-50%); z-index: 2001;
      display: flex; align-items: center; gap: 10px; max-width: calc(100vw - 480px);
      background: #ffc107; color: #000; font-size: 14px; padding: 4px 5px 4px 14px;
      border-radius: 999px; box-shadow: 0 2px 8px rgba(0, 0, 0, .35);
    }
    .impersonation-pill .who { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .impersonation-pill .btn { border-radius: 999px; white-space: nowrap; }
  `],
})
export class ImpersonationBannerComponent {
  private readonly impersonation = inject(ImpersonationService);
  readonly session = computed(() => this.impersonation.session());
  readonly stopping = signal(false);

  constructor() {
    const s = this.impersonation.session();
    if (!s) return;
    const delay = Math.max(0, new Date(s.expiration).getTime() - Date.now() - AUTO_STOP_BEFORE_MS);
    const timer = setTimeout(() => this.stop(), delay);
    inject(DestroyRef).onDestroy(() => clearTimeout(timer));
  }

  stop() {
    if (this.stopping()) return;
    this.stopping.set(true);
    this.impersonation.stop();
  }
}
